/**
 * Validation utilities for session code and player name.
 */

import i18next from 'i18next'

// Valid characters for session code (excluding confusing 0/O/1/I/L)
export const VALID_SESSION_CODE_CHARS = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789'

// Validation constraints
export const SESSION_CODE_LENGTH = 9
export const PLAYER_NAME_MIN_LENGTH = 1
export const PLAYER_NAME_MAX_LENGTH = 30

/**
 * Check if a session code is valid.
 * Valid: exactly 9 characters from VALID_SESSION_CODE_CHARS.
 */
export function isValidSessionCode(code: string): boolean {
  const normalized = normalizeSessionCode(code)
  
  if (normalized.length !== SESSION_CODE_LENGTH) {
    return false
  }
  
  return [...normalized].every(char => VALID_SESSION_CODE_CHARS.includes(char))
}

/**
 * Normalize session code: remove dashes, uppercase.
 */
export function normalizeSessionCode(code: string): string {
  return code.replace(/-/g, '').toUpperCase()
}

/**
 * Format session code for display: ABC-123-XYZ.
 */
export function formatSessionCode(code: string): string {
  const normalized = normalizeSessionCode(code)
  const parts: string[] = []
  
  for (let i = 0; i < normalized.length && i < 9; i += 3) {
    parts.push(normalized.slice(i, i + 3))
  }
  
  return parts.join('-')
}

/**
 * Check if a player name is valid.
 * Valid: 1-30 characters after trimming.
 */
export function isValidPlayerName(name: string): boolean {
  const trimmed = name.trim()
  return trimmed.length >= PLAYER_NAME_MIN_LENGTH && 
         trimmed.length <= PLAYER_NAME_MAX_LENGTH
}

/**
 * Get validation error message for session code (localized via i18next).
 * Returns null if valid.
 */
export function getSessionCodeError(code: string): string | null {
  const normalized = normalizeSessionCode(code)
  
  if (normalized.length === 0) {
    return null // Empty is not an error yet (user hasn't typed)
  }
  
  if (normalized.length < SESSION_CODE_LENGTH) {
    return i18next.t("validation.sessionCodeMissing", {
      length: SESSION_CODE_LENGTH,
      count: SESSION_CODE_LENGTH - normalized.length,
    })
  }
  
  if (normalized.length > SESSION_CODE_LENGTH) {
    return i18next.t("validation.sessionCodeTooLong", { length: SESSION_CODE_LENGTH })
  }
  
  const invalidChar = [...normalized].find(char => !VALID_SESSION_CODE_CHARS.includes(char))
  if (invalidChar) {
    return i18next.t("validation.invalidChar", { char: invalidChar })
  }
  
  return null
}

/**
 * Get validation error message for player name (localized via i18next).
 * Returns null if valid.
 */
export function getPlayerNameError(name: string): string | null {
  const trimmed = name.trim()
  
  if (trimmed.length === 0) {
    return null // Empty is not an error yet
  }
  
  if (trimmed.length > PLAYER_NAME_MAX_LENGTH) {
    return i18next.t("validation.nameTooLong", { max: PLAYER_NAME_MAX_LENGTH })
  }
  
  return null
}
